import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Sidebar from '../components/Sidebar';
import BackIconButton from '../components/BackIconButton';
import Modal from '../components/Modal';
import { OttoInlineLoading } from '../components/otto';
import { useCurrentInternalUser } from '../lib/currentUser';
import { formatDateBritish, todayIso } from '../lib/compliance';
import { signatureStandingFor, standingCountsAsCompliant } from '../lib/documentVersion';
import { chaseResignOutstanding } from '../lib/documentDrafts';
import { useActionToast } from '../components/ActionToast';
import { renderSubmissionHtml, coerceSchema } from '../lib/hostedForm';

// ============================================================================
// Document history — every version of ONE document (versioning brief B)
// ============================================================================
// §1 keeps three histories apart, and this is the first: VERSION history — one
// document, all suppliers. (DOCUMENT history is one supplier's stream at
// /compliance/:id/detail; the ACTIVITY log is system-wide.) They meet only
// where a supplier's row says "signed v4" and links to that version.
//
// The number that matters here is not "how many signed" but "how many of those
// signatures still stand". During a re-sign period those are the same people
// with two different meanings, so §3 splits every row into Still valid vs
// Historic rather than showing one total.
// ============================================================================

const FONT = "font-['Metropolis']";

const METHOD_LABEL = {
  upload: 'Supplier uploads a file',
  download_return: 'Download, sign, return',
  form: 'Hosted form',
  acknowledge: 'Read and acknowledge',
  pdf_sign: 'PDF with signature fields',
};

// Status treatment per §3 — the PILL TEXT carries the state; colour only
// reinforces it.
function versionStatus(version, isCurrent, standing) {
  if (isCurrent) return { label: 'Current', border: '#92D400', pill: 'text-fusion-forest bg-[#92D400]/12 border-[#92D400]/40' };
  if (standing.state === 'resign_due') {
    return {
      label: `Re-signature due ${formatDateBritish(standing.dueDate) || 'soon'}`,
      border: '#E89A2E',
      pill: 'text-[#7A4E10] bg-[#E89A2E]/12 border-[#E89A2E]/40',
    };
  }
  if (standing.state === 'invalid_now') {
    return { label: 'Re-signature required now', border: '#E0290A', pill: 'text-[#B02309] bg-[#E0290A]/10 border-[#E0290A]/40' };
  }
  if (standing.state === 'lapsed') {
    return { label: 'Re-signature overdue', border: '#E0290A', pill: 'text-[#B02309] bg-[#E0290A]/10 border-[#E0290A]/40' };
  }
  return { label: 'Superseded', border: '#DADCCD', pill: 'text-[#51534A] bg-[#51534A]/8 border-[#DADCCD]' };
}

export default function DocumentVersions() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);       // newest first, drafts excluded
  const [draft, setDraft] = useState(null);           // unpublished edit, if any
  const [signatures, setSignatures] = useState([]);   // { supplierId, supplierName, version, signedAt, signerName }
  const [publishers, setPublishers] = useState({});   // internal_users.id → name
  const [tab, setTab] = useState('versions');         // 'versions' | 'signed'
  const [showEarlier, setShowEarlier] = useState(false);
  const [viewing, setViewing] = useState(null);       // version row being previewed
  const [filterVersion, setFilterVersion] = useState('all');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [chasing, setChasing] = useState(null);   // version id mid-send
  const { notify, Toast } = useActionToast();

  // Chase everyone still on this version. The live version carries the reason
  // and deadline, so that's what the email quotes — not the old version they
  // signed. Rate-limited per supplier in chaseResignOutstanding; the result
  // says plainly how many were skipped for that reason.
  const runChase = useCallback(async (version, sigs) => {
    const liveVersion = versions.find(v => v.status === 'active') || version;
    setChasing(version.id);
    try {
      const supplierIds = [...new Set(sigs.map(s => s.supplierId).filter(Boolean))];
      const { sent, failed, skipped } = await chaseResignOutstanding(liveVersion, supplierIds);
      const detail = [];
      if (skipped) detail.push(`${skipped} skipped — already chased in the last 7 days, or no contact address`);
      if (failed) detail.push(`${failed} couldn't be emailed`);
      notify({
        eyebrow: 'Re-signature chase',
        title: sent
          ? `${sent} supplier${sent === 1 ? '' : 's'} chased`
          : 'Nobody chased',
        body: detail.join(' · ') || undefined,
        tone: failed ? 'danger' : sent ? 'success' : 'info',
      });
    } catch (e) {
      notify({ eyebrow: 'Chase failed', title: e.message || 'Could not send the chase emails.', tone: 'danger' });
    }
    setChasing(null);
  }, [versions, notify]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: anchor, error: anchorErr } = await supabase
      .from('fusion_documents').select('*').eq('id', docId).single();
    if (anchorErr || !anchor) { setError(anchorErr?.message || 'Document not found.'); setLoading(false); return; }

    // Versions of one document share a compliance_doc_key. Documents outside
    // the compliance library have no key, so fall back to the parent chain —
    // an anchor plus anything descended from it.
    const key = anchor.compliance_doc_key;
    const { data: family } = key
      ? await supabase.from('fusion_documents').select('*').eq('compliance_doc_key', key)
      : await supabase.from('fusion_documents').select('*')
          .or(`id.eq.${anchor.id},parent_document_id.eq.${anchor.id}`);

    const rows = (family || []).length ? family : [anchor];
    const published = rows.filter(r => r.status !== 'draft')
      .sort((a, b) => (Number(b.version_value) || 0) - (Number(a.version_value) || 0));
    setVersions(published);
    setDraft(rows.find(r => r.status === 'draft') || null);

    // Signatures across both tables, for every version of this document.
    const docKey = key || anchor.compliance_doc_key;
    const [subs, acks] = await Promise.all([
      docKey
        ? supabase.from('hosted_form_submissions')
            .select('supplier_id, document_version, signed_at, signer_name')
            .eq('document_key', docKey)
        : { data: [] },
      docKey
        ? supabase.from('document_acknowledgements')
            .select('supplier_id, document_version, acknowledged_at, signer_name, review_decision')
            .eq('document_key', docKey)
        : { data: [] },
    ]);

    const combined = [
      ...(subs.data || []).map(s => ({
        supplierId: s.supplier_id, version: s.document_version, signedAt: s.signed_at, signerName: s.signer_name,
      })),
      ...(acks.data || [])
        // A rejected acknowledgement was never accepted — counting it would
        // overstate who has signed.
        .filter(a => a.review_decision !== 'rejected')
        .map(a => ({
          supplierId: a.supplier_id, version: a.document_version, signedAt: a.acknowledged_at, signerName: a.signer_name,
        })),
    ];

    const supplierIds = [...new Set(combined.map(s => s.supplierId).filter(Boolean))];
    let namesById = {};
    if (supplierIds.length) {
      const { data: sups } = await supabase.from('suppliers').select('id, supplier_name').in('id', supplierIds);
      namesById = Object.fromEntries((sups || []).map(s => [s.id, s.supplier_name]));
    }
    setSignatures(combined.map(s => ({ ...s, supplierName: namesById[s.supplierId] || 'Unknown supplier' })));

    const publisherIds = [...new Set(published.map(v => v.published_by_user_id).filter(Boolean))];
    if (publisherIds.length) {
      const { data: users } = await supabase.from('internal_users').select('id, full_name').in('id', publisherIds);
      setPublishers(Object.fromEntries((users || []).map(u => [u.id, u.full_name])));
    }
    setLoading(false);
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  const current = versions.find(v => v.status === 'active') || null;
  const today = todayIso();

  // Per-version rollup: who signed it, and whether those signatures still stand.
  const rows = useMemo(() => versions.map(v => {
    const standing = signatureStandingFor(v, versions, { todayIso: today });
    const mine = signatures.filter(s => Number(s.version) === Number(v.version_value));
    const stillValid = standingCountsAsCompliant(standing.state) ? mine.length : 0;
    return { version: v, standing, signatures: mine, stillValid, historic: mine.length - stillValid };
  }), [versions, signatures, today]);

  // §8 — versions with nothing valid against them collapse behind a toggle so
  // a long history doesn't bury what's live.
  const primaryRows = rows.filter(r => r.version.status === 'active' || r.stillValid > 0 || r.signatures.length > 0);
  const earlierRows = rows.filter(r => !primaryRows.includes(r));

  // §4 — one row per supplier: the newest version they signed, and what they
  // owe. "The version list says 85 outstanding; this says WHICH 85."
  const signedRows = useMemo(() => {
    const bySupplier = new Map();
    for (const s of signatures) {
      const prev = bySupplier.get(s.supplierId);
      if (!prev || Number(s.version) > Number(prev.version)) bySupplier.set(s.supplierId, s);
    }
    return [...bySupplier.values()].map(s => {
      const versionRow = versions.find(v => Number(v.version_value) === Number(s.version));
      const standing = versionRow
        ? signatureStandingFor(versionRow, versions, { todayIso: today })
        : { state: 'valid', dueDate: null, reason: null };
      return { ...s, standing };
    }).sort((a, b) => (a.supplierName || '').localeCompare(b.supplierName || ''));
  }, [signatures, versions, today]);

  const visibleSignedRows = signedRows.filter(r => {
    if (filterVersion !== 'all' && Number(r.version) !== Number(filterVersion)) return false;
    if (outstandingOnly && r.standing.state === 'valid') return false;
    return true;
  });

  const outstandingCount = signedRows.filter(r => r.standing.state !== 'valid').length;

  // "View as published" renders from THAT version's own stored schema — never
  // from current content, which is the whole point of keeping versions.
  const viewingHtml = useMemo(() => {
    if (!viewing?.form_schema) return '';
    return renderSubmissionHtml(coerceSchema(viewing.form_schema), {
      fields: {}, ticks: {}, signerName: '', signedAtLabel: '',
      docLabel: viewing.document_name || 'Document', version: viewing.version_value || null,
    });
  }, [viewing]);

  const Pill = ({ children, className }) => (
    <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.13em] rounded-full border px-2.5 py-1 whitespace-nowrap ${className}`}>{children}</span>
  );

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentInternalUser} />
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-8 pt-8 pb-16 max-w-[1040px]">
          <div className="mb-4"><BackIconButton to="/documents" label="Back to Fusion Documents" /></div>

          {/* OttoInlineLoading, not OttoLoading: the sidebar, the back link and
              this column all stay put, so only a panel is filling — and there is
              a bar here for him to look at, which is the whole premise of the
              head-cock. OttoLoading's flying hover is for the full-page loads
              where Otto IS the scene (build brief 05 §6).

              Rendered OUTSIDE the ternary on purpose. It owns its own
              visibility — it returns null below the floor and while idle — and
              it needs to still be mounted when `active` goes false so the head
              can unwind. Put it inside the `loading ?` branch and the parent
              unmounts it the instant the request lands, so the outro never
              plays and he vanishes mid-cock. */}
          <OttoInlineLoading active={loading} message="Loading versions" />

          {loading ? null : error ? (
            <p className={`${FONT} text-sm text-[#E0290A]`}>{error}</p>
          ) : (
            <>
              {/* Header (§2) */}
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6 mb-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <h1 className={`${FONT} text-[24px] font-extrabold text-ink leading-tight`}>
                      {current?.document_name || versions[0]?.document_name || 'Document'}
                    </h1>
                    <p className={`${FONT} text-[13px] text-fusion-mid-grey mt-1`}>
                      {METHOD_LABEL[current?.satisfied_by || versions[0]?.satisfied_by] || 'Document'}
                      {' · '}{versions.length} version{versions.length === 1 ? '' : 's'}
                      {' · '}{signatures.length} signature{signatures.length === 1 ? '' : 's'} in total
                    </p>
                  </div>
                  {current && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button type="button"
                        onClick={() => navigate(`/documents/${current.id}/${current.satisfied_by === 'pdf_sign' ? 'pdf-fields' : 'build'}`)}
                        className={`${FONT} text-[11px] font-bold uppercase tracking-[0.14em] text-[#2E8FA1] bg-white border border-[#2E8FA1]/40 rounded-full px-4 py-2 hover:border-[#2E8FA1]`}>
                        Edit current
                      </button>
                    </div>
                  )}
                </div>

                {draft && (
                  <p className={`${FONT} text-[12px] text-[#7A4E10] bg-[#E89A2E]/[0.09] border border-[#E89A2E]/30 rounded-lg px-3 py-2 mt-4`}>
                    A draft of v{draft.version_value} is in progress — suppliers keep signing v{current?.version_value || 1} until it's published.
                  </p>
                )}
              </div>

              {/* Tabs (§2) */}
              <div role="tablist" aria-label="Document history" className="flex items-center gap-2 mb-4">
                {[['versions', `Versions (${versions.length})`], ['signed', `Who's signed what (${signedRows.length})`]].map(([key, label]) => (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={tab === key}
                    onClick={() => setTab(key)}
                    className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 border transition-colors ${
                      tab === key ? 'bg-[#2F6E14] text-white border-[#2F6E14]' : 'bg-white text-[#51534A] border-line hover:border-[#DADCCD]'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'versions' && (
                <>
                  {[...primaryRows, ...(showEarlier ? earlierRows : [])].map(({ version, standing, signatures: sigs, stillValid, historic }) => {
                    const isCurrent = version.status === 'active';
                    const status = versionStatus(version, isCurrent, standing);
                    return (
                      <div key={version.id} className="bg-white border border-[#E6EADB] rounded-2xl mb-3 overflow-hidden"
                        style={{ borderLeft: `3px solid ${status.border}` }}>
                        <div className="p-5">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2.5 flex-wrap">
                                <span className={`${FONT} text-[18px] font-extrabold text-ink`}>v{version.version_value || 1}</span>
                                <Pill className={status.pill}>{status.label}</Pill>
                              </div>
                              <p className={`${FONT} text-[12px] text-[#51534A] mt-1.5`}>
                                Published {formatDateBritish(String(version.published_at || version.created_at).slice(0, 10)) || '—'}
                                {version.published_by_user_id && publishers[version.published_by_user_id]
                                  ? ` by ${publishers[version.published_by_user_id]}`
                                  : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {version.form_schema && (
                                <button type="button" onClick={() => setViewing(version)}
                                  className={`${FONT} text-[11px] font-bold text-[#2E8FA1] bg-white border border-[#2E8FA1]/40 rounded-full px-3.5 py-1.5 hover:border-[#2E8FA1]`}>
                                  View as published
                                </button>
                              )}
                              {(standing.state === 'resign_due' || standing.state === 'lapsed' || standing.state === 'invalid_now') && sigs.length > 0 && (
                                <>
                                  <button type="button" onClick={() => { setTab('signed'); setFilterVersion(version.version_value); setOutstandingOnly(true); }}
                                    className={`${FONT} text-[11px] font-bold text-[#51534A] bg-white border border-line rounded-full px-3.5 py-1.5 hover:border-[#DADCCD]`}>
                                    See who
                                  </button>
                                  <button type="button" onClick={() => runChase(version, sigs)} disabled={chasing === version.id}
                                    className={`${FONT} text-[11px] font-bold text-white bg-[#E89A2E] border border-[#E89A2E] rounded-full px-3.5 py-1.5 hover:brightness-105 disabled:opacity-60`}>
                                    {chasing === version.id ? 'Sending…' : `Chase ${sigs.length}`}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* What changed — the publish reason lives here permanently (§3) */}
                          {version.change_reason && (
                            <div className="mt-3 rounded-lg bg-[#F7F9F0] border border-[#E6EADB] px-3 py-2.5">
                              <div className={`${FONT} text-[10px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-1`}>What changed</div>
                              <p className={`${FONT} text-[13px] text-ink`}>{version.change_reason}</p>
                            </div>
                          )}

                          {/* Signature split — same number, two meanings (§3) */}
                          <div className="flex items-center gap-5 mt-3.5">
                            <div>
                              <div className={`${FONT} text-[17px] font-extrabold text-ink tabular-nums`}>{stillValid}</div>
                              <div className={`${FONT} text-[10px] font-bold uppercase tracking-[0.14em] text-[#51534A]`}>Still valid</div>
                            </div>
                            <div>
                              <div className={`${FONT} text-[17px] font-extrabold text-[#7C7F6F] tabular-nums`}>{historic}</div>
                              <div className={`${FONT} text-[10px] font-bold uppercase tracking-[0.14em] text-[#51534A]`}>Historic</div>
                            </div>
                            {standing.state === 'resign_due' && standing.dueDate && (
                              <p className={`${FONT} text-[12px] text-[#7A4E10]`}>
                                Compliant until {formatDateBritish(standing.dueDate)} — then not.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {earlierRows.length > 0 && (
                    <button type="button" onClick={() => setShowEarlier(v => !v)}
                      className={`${FONT} text-[12px] font-bold text-[#2E8FA1] bg-transparent border-0 cursor-pointer hover:underline`}>
                      {showEarlier ? 'Hide earlier versions' : `Show ${earlierRows.length} earlier version${earlierRows.length === 1 ? '' : 's'} with no signatures`}
                    </button>
                  )}
                </>
              )}

              {tab === 'signed' && (
                <>
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <label className={`${FONT} text-[12px] text-[#51534A] flex items-center gap-2`}>
                      Version
                      <select value={filterVersion} onChange={e => setFilterVersion(e.target.value)}
                        className={`${FONT} bg-white border border-line rounded-lg px-2.5 py-1.5 text-[12px]`}>
                        <option value="all">All</option>
                        {versions.map(v => <option key={v.id} value={v.version_value}>v{v.version_value}</option>)}
                      </select>
                    </label>
                    <label className={`${FONT} text-[12px] text-[#51534A] flex items-center gap-2`}>
                      <input type="checkbox" checked={outstandingOnly} onChange={e => setOutstandingOnly(e.target.checked)} className="accent-[#009159]" />
                      Only those who owe a signature ({outstandingCount})
                    </label>
                  </div>

                  <div className="bg-white border border-[#E6EADB] rounded-2xl overflow-x-auto">
                    <table className="w-full text-left min-w-[640px]">
                      <thead>
                        <tr className="bg-[#F7F9F0] border-b border-[#E6EADB]">
                          {['Supplier', 'Version signed', 'Signed', 'Owes'].map(h => (
                            <th key={h} scope="col" className={`${FONT} text-[10px] font-bold uppercase tracking-[0.14em] text-[#51534A] px-4 py-2.5`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleSignedRows.length === 0 && (
                          <tr><td colSpan={4} className={`${FONT} text-[13px] text-[#7C7F6F] px-4 py-6`}>
                            {signedRows.length === 0 ? 'Nobody has signed this document yet.' : 'Nothing matches those filters.'}
                          </td></tr>
                        )}
                        {visibleSignedRows.map(r => (
                          <tr key={`${r.supplierId}-${r.version}`} className="border-b border-[#F0F2E8] last:border-0">
                            <td className="px-4 py-3">
                              <button type="button" onClick={() => navigate(`/suppliers/${r.supplierId}`)}
                                className={`${FONT} text-[13px] font-semibold text-[#2E8FA1] hover:text-active-green underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer text-left`}>
                                {r.supplierName}
                              </button>
                            </td>
                            <td className={`${FONT} text-[13px] text-ink px-4 py-3 tabular-nums`}>v{r.version || '—'}</td>
                            <td className={`${FONT} text-[13px] text-[#51534A] px-4 py-3`}>
                              {formatDateBritish(String(r.signedAt || '').slice(0, 10)) || '—'}
                            </td>
                            <td className="px-4 py-3">
                              {r.standing.state === 'valid' && <span className={`${FONT} text-[12px] text-[#009159] font-semibold`}>Nothing</span>}
                              {r.standing.state === 'resign_due' && (
                                <span className={`${FONT} text-[12px] text-[#7A4E10] font-semibold`}>
                                  Sign again by {formatDateBritish(r.standing.dueDate)}
                                </span>
                              )}
                              {r.standing.state === 'lapsed' && (
                                <span className={`${FONT} text-[12px] text-[#B02309] font-semibold`}>
                                  Overdue since {formatDateBritish(r.standing.dueDate)}
                                </span>
                              )}
                              {r.standing.state === 'invalid_now' && (
                                <span className={`${FONT} text-[12px] text-[#B02309] font-semibold`}>Sign again now</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* "View as published" — rendered from the version's OWN stored content */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} maxWidth={1100} ariaLabel="Version as published">
        <Modal.Header
          eyebrow={`v${viewing?.version_value || 1} as published`}
          title={viewing?.document_name || 'Document'}
          sub="Rendered from this version's own stored content, not from the current document."
          onClose={() => setViewing(null)}
        />
        <Modal.Body>
          <iframe title="Version as published" srcDoc={viewingHtml} className="w-full h-[70vh] border border-line rounded-lg bg-white" />
        </Modal.Body>
      </Modal>

      <Toast />
    </div>
  );
}
